import { Component, OnInit, ViewChild } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AmountInput } from '../../../core/shared/directives/amount-input.directive';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CraditService, FinancialAnalysisRow } from '../shared/services/cradit.service';
import {
  CovenantExportRow,
  DisbursementNoteExportService,
} from '../shared/services/disbursement-note-export.service';
import { Setup } from '../../setup/shared/service/setup';
import { MODULE_SEQ, PKL_GRP, QSTNR_SEQ } from '../../../core/shared/models/picklist.model';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { Document } from '../../leads/document/document';
import { ToastService } from '../../../core/shared/services/toast.service';
import { amountInWords } from '../shared/utils/amount-in-words.util';
import { CovenantCompliance } from './covenant-compliance/covenant-compliance';
import { GuarantorShare } from './guarantor-share/guarantor-share';
import { Table } from '../../shared/table/table';
import { Popup } from '../../../core/shared/components/popup/popup';
import { TableColumn } from '../../shared/models/table-models';
import {
  CovenantResponseDto,
  CovenantRow,
  CreditGuarantorsResponseDto,
  DisbursementGuarantorDto,
  GuarantorLimitUtilizedDto,
  ProposedDisbursementNoteDto,
} from '../shared/models/proposed-disbursement.model';

type NoteTab = 'note' | 'covenants' | 'financials' | 'cad' | 'guarantorShare' | 'attachments';

/** Latest Financials headings for the two year columns, newest first (as in the bank's note format). */
const FINANCIAL_PERIOD_LABELS = ['DISB_NOTE.LATEST_MANAGEMENT_ACCOUNTS', 'DISB_NOTE.LATEST_AUDITED_ACCOUNTS'];

/** One label/value cell of the read-only header grid. */
interface InfoField {
  label: string;
  value: string;

  /** Long values (amount in words, periods) take two columns. */
  wide?: boolean;

  /** Annexure field the API does not return yet — rendered as a dashed cell. */
  pending?: boolean;
}

@Component({
  selector: 'app-disbursement-note',
  imports: [
    CommonModule,
    FormsModule,
    CovenantCompliance,
    GuarantorShare,
    Table,
    Popup,
    TranslatePipe,
    AmountInput,
    Document,
  ],
  templateUrl: './disbursement-note.html',
  styleUrl: './disbursement-note.css',
})
export class DisbursementNote implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private creditService: CraditService,
    private toast: ToastService,
    private exportService: DisbursementNoteExportService,
    private setup: Setup,
    private pickListService: PickListService,
    private translate: TranslateService,
  ) {}

  // ================= NOTE =================
  prpsdDsbmntSeq = 0;
  note: ProposedDisbursementNoteDto | null = null;
  noteLoading = false;
  noteError = '';

  activeTab: NoteTab = 'note';

  /** `?mode=view` (e.g. from a dashboard task) opens the note read-only: no saving or submitting. */
  isViewMode = false;

  /** Roles that see the CAD tab — only when the note is opened from a dashboard task. */
  private readonly CAD_ROLES = ['LNLY_AVP', 'LNLY_VP', 'LNLY_CRCO', 'LNLY_CEO'];

  /**
   * CAD is shown only to CAD_ROLES, and only when the note is opened from a
   * dashboard task (`?fromTask=true`) — never from the sidebar. Set in ngOnInit.
   */
  showCadTab = false;

  /** Covenant Comment column: same audience as CAD (CAD_ROLES, opened from a dashboard task). */
  showCovenantComment = false;

  /** Only LNLY_AVP (from a task) may edit and save covenant comments; the others view them. */
  canEditCovenantComment = false;

  /** Only LNLY_AVP (from a task) may add / update CAD answers; the other CAD_ROLES view them. */
  canEditCad = false;

  /** Tab order drives the wizard footer: save as you go, submit on the last. */
  tabs: NoteTab[] = [];

  /** Only present while the covenants tab is rendered. */
  @ViewChild('covenantGrid') private covenantGrid?: CovenantCompliance;

  /** Same grid as covenants, driven by the CAD questionnaire. */
  @ViewChild('cadGrid') private cadGrid?: CovenantCompliance;

  get activeTabIndex(): number {
    return this.tabs.indexOf(this.activeTab);
  }

  get isFirstTab(): boolean {
    return this.activeTabIndex === 0;
  }

  get isLastTab(): boolean {
    return this.activeTabIndex === this.tabs.length - 1;
  }

  /** Note (comments), Covenants and CAD are the tabs holding editable data with a save endpoint. */
  get canSaveActiveTab(): boolean {
    // Comment is the only editable covenant field, so Save follows its permission.
    if (this.activeTab === 'covenants') {
      return this.canEditCovenantComment;
    }

    if (this.activeTab === 'cad') {
      return this.canEditCad && this.cadQstnrSeq > 0;
    }

    return !this.isViewMode && this.activeTab === 'note';
  }

  goNext(): void {
    if (!this.isLastTab) {
      this.setTab(this.tabs[this.activeTabIndex + 1]);
    }
  }

  goPrevious(): void {
    if (!this.isFirstTab) {
      this.setTab(this.tabs[this.activeTabIndex - 1]);
    }
  }

  /** Delegates to the active grid so the footer stays the single action bar. */
  saveActiveTab(): void {
    if (this.activeTab === 'note') {
      this.saveComments();
    } else if (this.activeTab === 'covenants') {
      this.covenantGrid?.save();
    } else if (this.activeTab === 'cad') {
      this.cadGrid?.save();
    }
  }

  // ================= ATTACHMENTS =================
  /** Uploads are saved with entyTyp 14 and entySeq = prpsdDsbmntSeq. */
  readonly ATTACHMENTS_MODULE_SEQ = MODULE_SEQ.PROPOSED_DISBURSEMENT;

  /** PDF, Word, Excel and images. */
  readonly ATTACHMENTS_ACCEPT =
    'image/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.csv,' +
    'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
    'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  // ================= USER INPUT =================
  /** The only figure captured on this screen; everything else is derived. */
  amountRecommended: number | null = null;
  comments = '';

  /** Comments are saved as a review against the proposed disbursement (entity type 14). */
  readonly REVIEW_ENTY_TYP = 14;
  savingComments = false;

  // ================= COVENANTS =================
  /** Fed to the covenant grid once the note resolves the credit proposal. */
  crdtPrpslSeq = 0;

  covenantsVisited = false;

  // ================= CAD =================
  /**
   * CAD questions live in their own setup questionnaire. Its seq is looked up
   * by name from `/qstnr/get-qstnr-list`, so no id is hardcoded here.
   */
  cadQstnrSeq = 0;
  cadVisited = false;
  cadLoading = false;
  cadError = '';

  // ================= GUARANTOR SHARE =================
  /** Kept alive after the first visit so the tab is not rebuilt on every switch. */
  guarantorShareVisited = false;

  submitting = false;
  showSubmitConfirm = false;

  // ================= FINANCIALS =================
  /**
   * The analysis can return well over a hundred indicators, so the rows are
   * flattened into the shared table, which brings search and pagination.
   * Period columns are built from the data, not hardcoded.
   */
  financialColumns: TableColumn[] = [];
  financialData: Record<string, string>[] = [];
  financialsLoading = false;
  financialsError = '';

  ngOnInit(): void {
    this.prpsdDsbmntSeq = Number(this.route.snapshot.paramMap.get('prpsdDsbmntSeq') ?? 0);
    this.isViewMode = this.route.snapshot.queryParamMap.get('mode') === 'view';

    const role = (localStorage.getItem('role') || localStorage.getItem('user_role') || '')
      .trim()
      .toUpperCase();
    const fromTask = this.route.snapshot.queryParamMap.get('fromTask') === 'true';

    this.showCadTab = this.CAD_ROLES.includes(role) && fromTask;
    this.showCovenantComment = this.showCadTab;
    this.canEditCovenantComment = fromTask && role === 'LNLY_AVP';
    this.canEditCad = this.canEditCovenantComment;
    this.tabs = (
      ['note', 'covenants', 'financials', 'cad', 'guarantorShare', 'attachments'] as NoteTab[]
    ).filter((tab) => tab !== 'cad' || this.showCadTab);

    if (this.prpsdDsbmntSeq <= 0) {
      this.noteError = 'DISB_NOTE.ERR_SEQ_MISSING';
      return;
    }

    this.loadNote();
  }

  // ================= LOAD =================
  private loadNote(): void {
    this.noteLoading = true;
    this.noteError = '';

    this.creditService.getProposedDisbursementNote(this.prpsdDsbmntSeq).subscribe({
      next: (response: any) => {
        this.note = response ?? null;
        this.noteLoading = false;

        if (!this.note) {
          this.noteError = 'DISB_NOTE.ERR_NOTE_NOT_FOUND';
          return;
        }

        // Pre-fill the input with whatever was recommended previously.
        this.amountRecommended =
          this.note.amntRcmnded != null ? Number(this.note.amntRcmnded) : null;
        this.comments = this.note.cmmnt ?? '';

        this.crdtPrpslSeq = Number(this.note.crdtPrpslSeq ?? 0);

        this.loadComments();

        if (this.crdtPrpslSeq > 0) {
          this.loadFinancials(this.crdtPrpslSeq);
        }
      },

      error: (error: any) => {
        console.error('Unable to load disbursement note:', error);

        this.note = null;
        this.noteLoading = false;
        this.noteError = 'DISB_NOTE.ERR_LOAD_NOTE';
      },
    });
  }

  /**
   * Rows of get-financial-highlights: a plain list, or wrapped in `data`
   * and/or `rows` (the saved Financial Analysis Details block shape).
   * Deleted rows are dropped.
   */
  private extractFinancialHighlightRows(response: unknown): FinancialAnalysisRow[] {
    const source = response as { data?: unknown; rows?: unknown } | null;
    const data = source?.data as { rows?: unknown } | undefined;

    const list = [source, data, source?.rows, data?.rows].find(Array.isArray) as
      | Record<string, unknown>[]
      | undefined;

    return (list ?? [])
      .filter((row) => !!row && typeof row === 'object' && Number(row['delFlg'] ?? 0) !== 1)
      .map((row) => row as unknown as FinancialAnalysisRow);
  }

  private loadFinancials(crdtPrpslSeq: number): void {
    this.financialsLoading = true;
    this.financialsError = '';

    this.creditService.getFinancialHighlights(crdtPrpslSeq).subscribe({
      next: (response: unknown) => {
        const table = this.buildFinancialTable(response);

        this.financialColumns = table.columns;
        this.financialData = table.data;

        this.financialsLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load financial highlights:', error);

        this.financialColumns = [];
        this.financialData = [];
        this.financialsLoading = false;
        this.financialsError = 'DISB_NOTE.ERR_LOAD_FINANCIALS';
      },
    });
  }

  /**
   * Latest Financials table (columns + flattened rows) from a
   * get-financial-highlights response — used by the tab and the report alike.
   */
  private buildFinancialTable(response: unknown): { columns: TableColumn[]; data: Record<string, string>[] } {
    const rows = this.extractFinancialHighlightRows(response);

    // Rows are shown exactly in the order the API returns them.
    const sorted = rows;

    // Column set is the union of every row's period keys, first seen first.
    const allPeriods: string[] = [];

    for (const row of sorted) {
      for (const period of Object.keys(row.periodValues ?? {})) {
        if (!allPeriods.includes(period)) {
          allPeriods.push(period);
        }
      }
    }

    // Only the latest two periods are shown, newest first (current year, then previous).
    const yearOf = (period: string): number => Number(period.match(/\d{4}/)?.[0] ?? 0);
    const periods = [...allPeriods].sort((first, second) => yearOf(second) - yearOf(first)).slice(0, 2);

    // Period labels come from the API and may contain spaces or dots, so
    // they are keyed positionally rather than used as property names.
    const columns: TableColumn[] = [
      { key: 'srNo', label: 'DISB_NOTE.S_NO' },
      { key: 'indicator', label: 'DISB_NOTE.INDICATOR' },
      // No monthly figures come from the API yet, so every row shows NA.
      { key: 'latestMonthly', label: 'DISB_NOTE.LATEST_MONTHLY' },
      ...periods.map((period, index) => ({
        key: `period${index}`,
        // Newest period = Latest Management Accounts, the one before = Latest Audited Accounts.
        label: FINANCIAL_PERIOD_LABELS[index] ?? period,
      })),
    ];

    const data = sorted.map((row, rowIndex) => {
      // `head` is the section heading the statement groups rows under
      // ("Portfolio Quality"); `dscr` is the line item ("PAR > 30 days").
      // Subtotal rows carry their title in `head` and leave `dscr` blank.
      const section = (row.head || '').trim();
      const description = (row.dscr || '').trim();

      const flat: Record<string, string> = {
        srNo: String(rowIndex + 1),
        section: section || '-',
        indicator: description || section || '-',
        latestMonthly: 'NA',
      };

      periods.forEach((period, index) => {
        flat[`period${index}`] = this.decimal(row.periodValues?.[period]);
      });

      // The table also searches properties that are not rendered as
      // columns, so the unformatted figures and the long description ride
      // along: "1103" then matches a cell displayed as "1,103", and the
      // description is searchable even when the short head is shown.
      periods.forEach((period, index) => {
        const raw = row.periodValues?.[period];

        flat[`period${index}Raw`] = raw == null ? '' : String(raw);
      });

      flat['dscrSearch'] = (row.dscr || '').trim();

      return flat;
    });

    return { columns, data };
  }

  // ================= HEADER GRID =================
  /** Every field the note endpoint returns, in printed-note reading order. */
  get headerFields(): InfoField[] {
    const note = this.note;

    if (!note) {
      return [];
    }

    return [
      { label: 'DISB_NOTE.BORROWER_NAME', value: note.brwrNm || '-' },
      { label: 'DISB_NOTE.FACILITY_NAME', value: note.fcltyNm || '-' },
      { label: 'DISB_NOTE.DISBURSEMENT_ID', value: this.text(note.prpsdDsbmntSeq) },
      { label: 'DISB_NOTE.CREDIT_PROPOSAL_ID', value: this.text(note.crdtPrpslSeq) },

      { label: 'DISB_NOTE.INSTALLMENT_NO', value: this.text(note.installmentNo) },
      { label: 'DISB_NOTE.TRANCHE', value: this.text(note.trnch) },
      { label: 'DISB_NOTE.APPROVAL_DATE', value: this.date(note.aprvlDt) },
      { label: 'DISB_NOTE.APPROVING_AUTHORITY', value: note.aprvngAthrty || '-' },

      { label: 'DISB_NOTE.AGREEMENT_DATE', value: this.date(note.agrmntDt) },
      { label: 'DISB_NOTE.AGREEMENT_AMOUNT', value: this.money(note.agrmntAmnt) },
      { label: 'DISB_NOTE.AGREEMENT_PERIOD', value: note.agrmntPeriod || '-' },
      { label: 'DISB_NOTE.DISBURSEMENT_PERIOD', value: note.dsbmntPeriod || '-' },

      { label: 'DISB_NOTE.APPROVED_EXPOSURE', value: this.money(note.aprvdExposure) },
      { label: 'DISB_NOTE.CURRENT_OUTSTANDING_EXPOSURE', value: this.money(note.crntOutStndngExposure) },
      { label: 'DISB_NOTE.AMOUNT_RECOMMENDED_SAVED', value: this.money(note.amntRcmnded) },
      { label: 'DISB_NOTE.EXPOSURE_POST_DISBURSEMENT_SAVED', value: this.money(note.exposurePostDsbmnt) },

      { label: 'DISB_NOTE.AMOUNT_IN_WORDS_SAVED', value: note.amntInWords || '-', wide: true },

      // Annexure I monitoring fields.
      { label: 'DISB_NOTE.PMD_LAST_FIELD_VISIT_DATE', value: this.date(note.pmdLstVstFldDt) },
      { label: 'DISB_NOTE.LAST_INSTALMENT_DUE_DATE', value: this.date(note.lstInstDueDt) },
      { label: 'DISB_NOTE.LAST_PAYMENT_RECEIVED_DATE', value: this.date(note.lstPymntRcvdDt) },
      { label: 'DISB_NOTE.PREVIOUS_IRS_RATING', value: this.text(note.prevIrsRtng) },
      { label: 'DISB_NOTE.LATEST_IRS_RATING', value: this.text(note.ltstIrsRtng) },
    ];
  }

  private text(value: unknown): string {
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  /** Dates arrive as ISO strings (2026-08-06); the note shows them as 06-Aug-2026. */
  private date(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    try {
      return formatDate(value, 'dd-MMM-yyyy', 'en-US');
    } catch {
      return String(value);
    }
  }

  private money(value: number | null | undefined): string {
    return value == null ? '-' : Number(value).toLocaleString('en-US');
  }

  /**
   * Formatted here rather than via the table's `currency` type, which renders
   * anything not greater than zero as "-" — that would turn a PAR of 0.07 into
   * "0" and a genuine zero balance into a blank.
   */
  private decimal(value: number | null | undefined): string {
    if (value == null || Number.isNaN(Number(value))) {
      return '-';
    }

    return Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  // ================= CALCULATION =================
  /**
   * `aprvdExposure` comes back null on live proposals. Without a ceiling there
   * is no headroom to show and nothing to breach, so the bar and the limit
   * check are suppressed rather than computed against zero.
   */
  get hasApprovedExposure(): boolean {
    return this.note?.aprvdExposure != null && Number(this.note.aprvdExposure) > 0;
  }

  get approvedExposure(): number {
    return Number(this.note?.aprvdExposure ?? 0);
  }

  get currentOutstanding(): number {
    return Number(this.note?.crntOutStndngExposure ?? 0);
  }

  /** Exposure post disbursement = current outstanding + amount recommended. */
  get exposurePostDisbursement(): number {
    return this.currentOutstanding + Number(this.amountRecommended ?? 0);
  }

  get headroom(): number {
    return this.approvedExposure - this.exposurePostDisbursement;
  }

  get utilisationPercent(): number {
    if (!this.hasApprovedExposure) {
      return 0;
    }

    const percent = (this.exposurePostDisbursement / this.approvedExposure) * 100;

    return Math.min(Math.max(percent, 0), 100);
  }

  get exceedsApprovedExposure(): boolean {
    return this.hasApprovedExposure && this.exposurePostDisbursement > this.approvedExposure;
  }

  get amountInWordsText(): string {
    return amountInWords(this.amountRecommended);
  }

  get canSubmit(): boolean {
    return Number(this.amountRecommended ?? 0) > 0 && !this.exceedsApprovedExposure;
  }

  // ================= RECOMMENDED AMOUNT =================
  savingAmount = false;

  /** POST /dsbmt/sv-dsbmnt-adj — saves the amount recommended on this screen. */
  saveRecommendedAmount(): void {
    if (this.savingAmount || this.prpsdDsbmntSeq <= 0) {
      return;
    }

    if (!this.canSubmit) {
      this.toast.error(
        this.exceedsApprovedExposure
          ? 'DISB_NOTE.ERR_EXCEEDS_CORRECT_ON_TAB'
          : 'DISB_NOTE.ERR_ENTER_AMOUNT_ON_TAB',
      );
      return;
    }

    this.savingAmount = true;

    this.creditService
      .saveRecommendedAmount({
        prpsdDsbmntSeq: this.prpsdDsbmntSeq,
        rcmndedAmnt: Number(this.amountRecommended),
      })
      .subscribe({
        next: () => {
          this.savingAmount = false;
          this.toast.success('DISB_NOTE.AMOUNT_SAVED');
        },
        error: (error: unknown) => {
          console.error('Unable to save recommended amount:', error);
          this.savingAmount = false;
          this.toast.error('DISB_NOTE.ERR_SAVE_AMOUNT');
        },
      });
  }

  // ================= COMMENTS (review) =================
  /** Patches the latest saved review comment into the Comments box. */
  private loadComments(): void {
    this.creditService.getRoReviews(this.prpsdDsbmntSeq, this.REVIEW_ENTY_TYP).subscribe({
      next: (response: any) => {
        const data = response?.data ?? response;
        const list: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : data && typeof data === 'object'
              ? [data]
              : [];

        const latest = [...list]
          .filter((row) => row && Number(row.delFlg ?? 0) !== 1)
          .sort(
            (first, second) =>
              new Date(second.crtdDt ?? second.lastUpdDt ?? 0).getTime() -
              new Date(first.crtdDt ?? first.lastUpdDt ?? 0).getTime(),
          )[0];

        if (latest?.cmmnt != null) {
          this.comments = String(latest.cmmnt);
        }
      },
      error: (error: unknown) => {
        console.error('Unable to load disbursement note comments:', error);
      },
    });
  }

  saveComments(): void {
    if (this.savingComments || this.prpsdDsbmntSeq <= 0) {
      return;
    }

    const cmmnt = this.comments.trim();

    if (!cmmnt) {
      this.toast.error('DISB_NOTE.ERR_COMMENTS_REQUIRED');
      return;
    }

    this.savingComments = true;

    this.creditService
      .saveEntityReview({
        entySeq: this.prpsdDsbmntSeq,
        entyType: this.REVIEW_ENTY_TYP,
        cmmnt,
      })
      .subscribe({
        next: () => {
          this.savingComments = false;
          this.toast.success('DISB_NOTE.COMMENTS_SAVED');
          this.loadComments();
        },
        error: (error: any) => {
          console.error('Unable to save disbursement note comments:', error);
          this.savingComments = false;
          this.toast.error(error?.error?.message || 'DISB_NOTE.ERR_SAVE_COMMENTS');
        },
      });
  }

  // ================= ACTIONS =================
  setTab(tab: NoteTab): void {
    if (!this.tabs.includes(tab)) {
      return;
    }

    this.activeTab = tab;

    // The covenant grid loads 1 + N + 1 requests, so it is built on first visit
    // rather than on page load — and kept alive afterwards to preserve edits.
    if (tab === 'covenants') {
      this.covenantsVisited = true;
    }

    if (tab === 'guarantorShare') {
      this.guarantorShareVisited = true;
    }

    if (tab === 'cad') {
      this.cadVisited = true;
      this.resolveCadQuestionnaire();
    }
  }

  /** CAD questionnaire seq, looked up by name once and then cached. */
  private findCadQstnrSeq(): Observable<number> {
    if (this.cadQstnrSeq > 0) {
      return of(this.cadQstnrSeq);
    }

    return this.setup.getQuestionnaireList().pipe(
      map((response: any) => {
        const list: any[] = Array.isArray(response) ? response : response?.data || [];

        const cad = list.find(
          (item) =>
            !item?.delFlg &&
            /\bCAD\b|credit\s+administration/i.test(String(item?.qstnrNm ?? '')),
        );

        return Number(cad?.qstnrSeq ?? 0);
      }),
    );
  }

  private resolveCadQuestionnaire(): void {
    if (this.cadQstnrSeq > 0 || this.cadLoading) {
      return;
    }

    this.cadLoading = true;
    this.cadError = '';

    this.findCadQstnrSeq().subscribe({
      next: (qstnrSeq) => {
        this.cadQstnrSeq = qstnrSeq;
        this.cadLoading = false;

        if (this.cadQstnrSeq <= 0) {
          this.cadError = 'DISB_NOTE.ERR_CAD_QUESTIONNAIRE_NOT_FOUND';
        }
      },

      error: (error: any) => {
        console.error('Unable to load questionnaire list:', error);

        this.cadLoading = false;
        this.cadError = 'DISB_NOTE.ERR_LOAD_CAD_QUESTIONNAIRE';
      },
    });
  }

  goBack(): void {
    this.router.navigate(['/proposed-disbursement']);
  }

  // ================= REPORT EXPORT =================
  /** True while the covenant list is being fetched for the report. */
  downloading = false;

  downloadReport(): void {
    if (!this.note || this.downloading) {
      return;
    }

    this.downloading = true;

    const creditGuarantors$ = this.crdtPrpslSeq > 0
      ? this.creditService
          .getCreditGuarantors(this.crdtPrpslSeq)
          .pipe(catchError(() => of(null)))
      : of(null);

    forkJoin({
      covenantRows: this.getCovenantExportRows(),
      cadRows: this.getCadExportRows(),
      guarantorLimits: this.creditService
        .getGuarantorLimitUtilized(this.prpsdDsbmntSeq)
        .pipe(catchError(() => of([] as GuarantorLimitUtilizedDto[]))),
      // The client's outstanding is fetched once per repayment term found
      // among the guarantors (17 Conventional, 18 Islamic, ...).
      guarantorsAndOutstanding: creditGuarantors$.pipe(
        switchMap((creditGuarantors) =>
          this.getClientOutstandingByTerm(creditGuarantors).pipe(
            map((clientOutstanding) => ({ creditGuarantors, clientOutstanding })),
          ),
        ),
      ),
      guarantorShares: this.creditService
        .getDisbursementGuarantors(this.prpsdDsbmntSeq)
        .pipe(catchError(() => of([] as DisbursementGuarantorDto[]))),
      // Latest Financials fetched fresh, so the report never depends on the tab
      // having loaded first; the tab's table is the fallback if the call fails.
      financials:
        this.crdtPrpslSeq > 0
          ? this.creditService.getFinancialHighlights(this.crdtPrpslSeq).pipe(
              map((response) => this.buildFinancialTable(response)),
              catchError(() => of({ columns: this.financialColumns, data: this.financialData })),
            )
          : of({ columns: this.financialColumns, data: this.financialData }),
      // GET /dsbmnt-note/get-prpsd-dsbmnt/{prpsdDsbmntSeq} — fetched fresh so the
      // report carries the latest figures (incl. the Direct Exposure panel).
      reportNote: this.creditService
        .getProposedDisbursementNote(this.prpsdDsbmntSeq)
        .pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ covenantRows, cadRows, guarantorLimits, guarantorsAndOutstanding, guarantorShares, financials, reportNote }) => {
        const { creditGuarantors, clientOutstanding } = guarantorsAndOutstanding;

        this.downloading = false;
        this.exportReport(
          covenantRows,
          cadRows,
          Array.isArray(guarantorLimits) ? guarantorLimits : [],
          creditGuarantors,
          Array.isArray(guarantorShares) ? guarantorShares : [],
          reportNote,
          clientOutstanding,
          financials,
        );
      },

      error: (error: any) => {
        console.error('Unable to load covenants for the report:', error);

        this.downloading = false;
        this.toast.error('DISB_NOTE.ERR_LOAD_COVENANTS_REPORT');
      },
    });
  }

  /**
   * GET /payment/recovery/get-clnt-otstndng/{clntSeq}/{fcltyTypKey}, called once
   * for every distinct `rpymntTrm` in the guarantors (e.g. 17 and 18 -> two calls).
   * A failed call yields a null response so the report still downloads.
   */
  private getClientOutstandingByTerm(
    creditGuarantors: CreditGuarantorsResponseDto | null,
  ): Observable<{ fcltyTypKey: number; response: unknown }[]> {
    const clntSeq = Number(creditGuarantors?.clntSeq ?? 0);
    const terms = [
      ...new Set(
        (creditGuarantors?.grntrs ?? [])
          .map((grntr) => Number(grntr.rpymntTrm))
          .filter((term) => Number.isFinite(term) && term > 0),
      ),
    ];

    if (clntSeq <= 0 || terms.length === 0) {
      return of([]);
    }

    return forkJoin(
      terms.map((fcltyTypKey) =>
        this.creditService.getClientOutstanding(clntSeq, fcltyTypKey).pipe(
          catchError(() => of(null)),
          map((response) => ({ fcltyTypKey, response })),
        ),
      ),
    );
  }

  private exportReport(
    covenantRows: CovenantExportRow[],
    cadRows: CovenantExportRow[],
    guarantorLimits: GuarantorLimitUtilizedDto[],
    creditGuarantors: CreditGuarantorsResponseDto | null,
    guarantorShares: DisbursementGuarantorDto[],
    reportNote: ProposedDisbursementNoteDto | null,
    clientOutstanding: { fcltyTypKey: number; response: unknown }[],
    financials: { columns: TableColumn[]; data: Record<string, string>[] },
  ): void {
    // The freshly fetched note wins; the one loaded with the page is the fallback.
    const note = reportNote ?? this.note;

    if (!note) {
      return;
    }

    // Same value columns as the Latest Financials tab: Latest Monthly, then the years.
    const valueColumns = financials.columns.filter(
      (column) => column.key === 'latestMonthly' || column.key.startsWith('period'),
    );
    // Same headings as the tab (translation keys; raw period labels pass through).
    const financialPeriods = valueColumns.map((column) => this.translate.instant(column.label));
    const financialRows = financials.data.map((row) => ({
      indicator: row['indicator'] ?? '-',
      values: valueColumns.map((column) => row[column.key] ?? '-'),
    }));

    this.exportService.download({
      note,
      amountRecommended: this.amountRecommended,
      amountInWordsText: this.amountInWordsText,
      exposurePostDisbursement: this.exposurePostDisbursement,
      comments: this.comments,
      covenantRows,
      cadRows,
      financialPeriods,
      financialRows,
      guarantorLimits,
      creditGuarantors,
      guarantorShares,
      clientOutstanding,
    });
  }

  /**
   * Covenant rows for the report. When the Covenants tab has been opened, its
   * grid is the source (so unsaved edits are included). Otherwise the grid was
   * never built, so the setup-driven covenant list and the saved responses
   * are fetched directly — the report must not depend on a tab visit.
   */
  /**
   * Covenant rows for the report, always fetched fresh from the same APIs as
   * the Covenants tab (selected covenants, questionnaire, saved responses) —
   * so the report never depends on the tab having been opened. Status and
   * remarks are not editable there, so nothing unsaved is lost.
   */
  private getCovenantExportRows(): Observable<CovenantExportRow[]> {
    if (this.crdtPrpslSeq <= 0) {
      return of([]);
    }

    return this.buildQuestionnaireExportRows(
      QSTNR_SEQ.COVENANT,
      this.creditService.getCovenantResponses(this.crdtPrpslSeq).pipe(catchError(() => of([]))),
      this.creditService.getCovenantQuestions(this.crdtPrpslSeq).pipe(catchError(() => of([]))),
    );
  }

  /**
   * CAD rows for the report's "CAD" sheet — the CAD tab's grid when it has been
   * opened (unsaved edits included), otherwise the CAD questionnaire plus the
   * saved `get-cad-rsp` answers, mapped onto the covenant response shape.
   */
  private getCadExportRows(): Observable<CovenantExportRow[]> {
    const gridRows = this.cadGrid?.rows ?? [];

    if (gridRows.length) {
      return of(this.gridToExportRows(gridRows));
    }

    const savedResponses: Observable<CovenantResponseDto[]> = this.creditService
      .getCadResponses(this.prpsdDsbmntSeq)
      .pipe(
        map((response: any) =>
          (Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [])
            .filter((item: any) => Number(item?.delFlg ?? 0) !== 1)
            .map((item: any) => ({
              crdtCnvntRspSeq: Number(item.cadRspSeq),
              crdtPrpslSeq: this.crdtPrpslSeq,
              qsntnSeq: Number(item.qstnSeq),
              ansVal: item.answrVal ?? '',
              rmrks: item.rmrks ?? '',
              delFlg: 0,
            })),
        ),
        catchError(() => of([])),
      );

    return this.findCadQstnrSeq().pipe(
      catchError(() => of(0)),
      switchMap((qstnrSeq) =>
        qstnrSeq > 0
          ? this.buildQuestionnaireExportRows(qstnrSeq, savedResponses)
          : of([] as CovenantExportRow[]),
      ),
    );
  }

  private gridToExportRows(
    rows: CovenantRow[],
    status: (row: CovenantRow) => string = (row) => row.ansVal,
  ): CovenantExportRow[] {
    return rows.map((row, index) => ({
      srNo: index + 1,
      description: row.question,
      complianceStatus: status(row) || '-',
      remarks: row.rmrks || '-',
    }));
  }

  /** Questionnaire questions joined with saved answers, in grid order. */
  private buildQuestionnaireExportRows(
    qstnrSeq: number,
    responses$: Observable<unknown>,
    selections$: Observable<unknown> = of([]),
  ): Observable<CovenantExportRow[]> {
    return forkJoin({
      questions: this.setup.getQuestionList(qstnrSeq),
      responses: responses$,
      selections: selections$,
      // Ref group 46 labels for `stsKey`, the status Monitoring saves (same as the grid).
      statuses: this.pickListService.loadCodesAsync([PKL_GRP.COVENANT_STATUS]).pipe(catchError(() => of(null))),
    }).pipe(
      switchMap(({ questions, responses, selections }) => {
        const statusLabel = (stsKey: unknown): string =>
          Number(stsKey) > 0
            ? String(
                this.pickListService
                  .getOptionsSync(PKL_GRP.COVENANT_STATUS)
                  .find((option) => Number(option.value) === Number(stsKey))?.label ?? '',
              )
            : '';

        // Only the covenants selected on the proposal, as the grid lists them
        // (no saved selection keeps the full list).
        const selectedSeqs = new Set(
          (Array.isArray(selections) ? selections : [])
            .filter((selected: any) => Number(selected?.delFlg ?? 0) !== 1)
            .map((selected: any) => Number(selected?.qstnSeq)),
        );

        // Same filtering/ordering as the covenant grid, so both print alike.
        const covenantQuestions: any[] = (Array.isArray(questions) ? questions : [])
          .filter(
            (question) => !question?.delFlg && (!selectedSeqs.size || selectedSeqs.has(Number(question.qstSeq))),
          )
          .sort(
            (first, second) =>
              Number(first.qstSortOrdr ?? first.qstSeq) - Number(second.qstSortOrdr ?? second.qstSeq),
          );

        // Same parsing as the grid: a bare array or one wrapped in `data` /
        // `responses`; the question seq may come as `qsntnSeq` or `qstnSeq`.
        const responseList: any[] = Array.isArray(responses)
          ? responses
          : Array.isArray((responses as any)?.data)
            ? (responses as any).data
            : Array.isArray((responses as any)?.responses)
              ? (responses as any).responses
              : [];
        const saved: CovenantResponseDto[] = responseList
          .filter((response) => Number(response?.delFlg ?? 0) !== 1)
          .map((response) => ({ ...response, qsntnSeq: Number(response?.qsntnSeq ?? response?.qstnSeq) }));

        const savedByQuestion = new Map<number, CovenantResponseDto>();

        saved.forEach((response) => savedByQuestion.set(Number(response.qsntnSeq), response));

        if (!covenantQuestions.length) {
          return of([] as CovenantExportRow[]);
        }

        // A saved answer can be the option text or its answer sequence; the
        // option list is only needed to translate the sequence into text.
        const answerCalls = covenantQuestions.map((question) => {
          const savedValue = String(savedByQuestion.get(Number(question.qstSeq))?.ansVal ?? '').trim();

          return /^\d+$/.test(savedValue)
            ? this.setup.getAnswerList(question.qstSeq).pipe(catchError(() => of([])))
            : of([]);
        });

        return forkJoin(answerCalls).pipe(
          map((answersPerQuestion: any[][]) =>
            covenantQuestions.map((question, index) => {
              const response = savedByQuestion.get(Number(question.qstSeq));
              const savedValue = String(response?.ansVal ?? '').trim();
              const option = (answersPerQuestion[index] || []).find(
                (answer: any) => !answer?.delFlg && String(answer.answrSeq) === savedValue,
              );

              return {
                srNo: index + 1,
                description: String(question.qstStr ?? '').trim(),
                // The answer, else the status Monitoring saved — as the Covenants grid shows it.
                complianceStatus:
                  (option ? String(option.answrStr ?? '') : savedValue) || statusLabel(response?.stsKey) || '-',
                remarks: String(response?.rmrks ?? '').trim() || '-',
              };
            }),
          ),
        );
      }),
    );
  }

  /** Opens the confirmation; submitting is a final action, not a draft save. */
  askToSubmit(): void {
    if (!this.canSubmit) {
      this.toast.error('DISB_NOTE.ERR_INVALID_RECOMMENDED_AMOUNT');
      return;
    }

    if (!this.validateCadAnswered()) {
      return;
    }

    this.showSubmitConfirm = true;
  }

  /**
   * The note can only be submitted once every CAD question has an answer.
   * Submit lives on the CAD tab, so the grid is normally built by now; if it
   * isn't (still loading, or the questionnaire wasn't found) submit is blocked.
   * Roles without the CAD tab are not held to it.
   */
  private validateCadAnswered(): boolean {
    if (!this.showCadTab) {
      return true;
    }

    const grid = this.cadGrid;

    if (this.cadLoading || !grid || grid.loading) {
      this.toast.error('DISB_NOTE.ERR_CAD_NOT_LOADED');
      return false;
    }

    const unanswered = grid.rows.length - grid.answeredCount;

    if (unanswered > 0) {
      this.toast.error(
        this.translate.instant('DISB_NOTE.ERR_CAD_UNANSWERED', {
          count: unanswered,
          total: grid.rows.length,
        }),
      );
      return false;
    }

    return true;
  }

  submitNote(): void {
    if (!this.canSubmit || this.submitting || !this.validateCadAnswered()) {
      return;
    }

    this.submitting = true;

    this.creditService
      .submitDisbursementNote(this.prpsdDsbmntSeq, {
        amntRcmnded: Number(this.amountRecommended ?? 0),
        amntInWords: this.amountInWordsText,
        cmmnt: this.comments,
      })
      .subscribe({
        next: (response: any) => {
          this.submitting = false;

          // A 200 can still carry an error body: the error interceptor has
          // already toasted it and flagged the body, so just stop here.
          if (response?.__hasError) {
            return;
          }

          if (response?.error) {
            this.toast.error(response.error);
            return;
          }

          this.toast.success(response?.message || 'DISB_NOTE.SUBMITTED_SUCCESS');

          // Back to the Proposed Disbursement listing once submitted.
          this.goBack();
        },

        error: (error: any) => {
          console.error('Disbursement note submission failed:', error);

          this.submitting = false;

          this.toast.error(
            error?.error?.message || error?.error?.error || 'DISB_NOTE.ERR_SUBMIT',
          );
        },
      });
  }
}
